import {PlayerData_ContainerInfo, PlayerData_DetailInfo} from "../dto/com.rico.sb2.service.positions";
import {UpdateContainerMessage} from "../dto/com.rico.sb2.message";
import {rem100} from "../lib/rem100";
import {ContainerState, ContainerType} from "../dto/com.rico.sb2.entity.detail";
import {ProcessControlService_Mode, TelemetryMessageProcessor_BathTelemetry} from "../dto/com.rico.sb2.service";
import {coalesce, nullWhenNaN, zeroWhenNaN} from "../lib/coalesce";
import {durationTextAround, msToDuration} from "../lib/timeFunctions";
import {Messages} from "../messages/Messages";
import {padLeft} from "../lib/stringFunctions";
import {Program_State, Program_Type} from "../dto/com.rico.sb2.entity.program";
import {currentToString, temperatureToString, voltageToString} from "../lib/formatTool";
import {moment} from '../boot'
import {getCurrentDate} from "../dto/com.rico.sb2.service.timeprovider";
import {AreaUnitFormatter} from "../lib/AreaUnitFormatter";

const messages = new Messages();

export interface ContainerDataSupplier {
	get data(): PlayerData_ContainerInfo

	get update(): UpdateContainerMessage | null

	get serviceMode(): ProcessControlService_Mode | null

	getPositionTitle(pos: number | null): string

	getPositionShortTitle(pos: number | null): string

	getPositionTelemetry(pos: number | null): TelemetryMessageProcessor_BathTelemetry | null

	isLoadingPosition(pos: number | null): boolean
}

export class ContainerDataAdapter {
	private readonly supplier: ContainerDataSupplier

	constructor(supplier: ContainerDataSupplier) {
		this.supplier = supplier
	}

	get serviceMode(): ProcessControlService_Mode | null {
		return this.supplier.serviceMode
	}

	get atLoadingPosition(): boolean {
		return this.supplier.isLoadingPosition(this.position);
	}

	get id() {
		return this.supplier.data.id
	}

	get number() {
		const text = rem100(this.supplier.data.id)
		return text.length == 0 ? text : padLeft(text, 2, '0');
	}

	get type(): ContainerType {
		return coalesce(this.supplier.data.type, ContainerType.NOT_DEFINED)
	}

	get state(): ContainerState | null {
		if (this.supplier.update) return this.supplier.update.state
		return this.supplier.data.state
	}

	stateString(messages: Messages): string {
		return this.state == null ? `` : messages.get(`container.state.${this.state}`);
	}

	get position(): number | null {
		if (this.supplier.update) return this.supplier.update.position
		return this.supplier.data.position
	}

	get transport(): number | null {
		if (this.supplier.update) return this.supplier.update.transport
		return null
	}

	get programProcess(): string {
		return coalesce(this.supplier.data.programProcess, '')
	}

	get programProcessId(): number | null {
		return this.supplier.data.programProcessId;
	}

	get programCoating(): string {
		return coalesce(this.supplier.data.programCoating, '')
	}

	get programStep(): number | null {
		return null
	}

	get programActions(): string[] | null {
		return this.supplier.data.programActions
	}

	get programActionMinTs(): number[] | null {
		return this.supplier.data.programActionMinTs
	}

	get programActionMaxTs(): number[] | null {
		return this.supplier.data.programActionMaxTs
	}

	get programCurrentAction(): string | null {
		const actions = this.programActions
		const step = this.programStep
		if (actions == null || step == null) return null
		return actions[step] || null
	}

	get areaString(): string {
		const containerUnit = AreaUnitFormatter.containerUnit()
		const areaDm = this.supplier.data.areaDm || 0
		const area = AreaUnitFormatter.fromDecimeters(areaDm, containerUnit)
		if (area == null)
			return '';
		return `${AreaUnitFormatter.unitFixed(area, containerUnit)} ${AreaUnitFormatter.unitLabel(containerUnit)}`;
	}

	get owner(): string {
		const update = this.supplier.update
		if (update && update.owner) return update.owner
		return coalesce(this.supplier.data?.owner, '')
	}

	get createDate(): string {
		return coalesce(this.supplier.data?.createDate, '')
	}

	get programStartTime(): number | null {
		return this.supplier.update?.startProgramTime || null
	}

	get programStartString(): string {
		const time = this.programStartTime
		if (time == null) return '';
		return moment(time).format('DD.MM.YYYY HH:mm');
	}

	get programFinishTime(): number | null {
		return this.supplier.update?.finishProgramTime || null
	}

	get programFinishString(): string {
		const time = this.programFinishTime
		if (time == null) return '';
		return moment(time).format('DD.MM.YYYY HH:mm');
	}

	get programStartPosition(): number | null {
		return null
	}

	get programTargetPosition(): number | null {
		return this.supplier.update?.finishProgramPosition || null
	}

	get programPlannedIn(): string {
		return ''
	}

	get programStartPositionString(): string {
		return coalesce(this.programStartPosition?.toString(), '');
	}

	get currentString() {
		const telemetry = this.supplier.getPositionTelemetry(this.position)
		if (!telemetry || telemetry.current == null) return ''
		return currentToString(telemetry.current);
	}

	get voltageString() {
		const telemetry = this.supplier.getPositionTelemetry(this.position)
		if (!telemetry || telemetry.voltage == null) return ''
		return voltageToString(telemetry.voltage);
	}

	get temperatureString() {
		const telemetry = this.supplier.getPositionTelemetry(this.position)
		if (!telemetry || telemetry.temp == null) return ''
		return temperatureToString(telemetry.temp);
	}

	get details(): PlayerData_DetailInfo[] {
		return this.supplier.data.details || []
	}

	detailCodes(limit: number): string {
		const list = this.details;
		if (!list.length) return '';
		const delimiter = ', ';
		const line = list
			.map(it => it.code)
			.sort((a, b) => a.localeCompare(b))
			.slice(0, limit)
			.join(delimiter)
		if (list.length > limit) {
			return line + messages.get('ContainerListItem.clickMenu.detailListMore', list.length - limit);
		}
		return line;
	}

	get programState(): Program_State | null {
		const update = this.supplier.update
		if (update) return update.lastProgramState
		return this.supplier.data.programState
	}

	get programByProcess(): boolean {
		const update = this.supplier.update
		if (update) return update.lastProgramType == Program_Type.BY_PROCESS
		return false
	}

	get overexposure(): boolean {
		return this.overexposureMs != null;
	}

	/**
	 * Вернем точку начала передержки (в миллисекундах), если она уже случилась
	 */
	get overexposureMs(): number | null {
		const update = this.supplier.update;
		if (!update) return null;

		const overexposureTime = update.overexposureTime;
		return (overexposureTime != null && overexposureTime + 1000 < getCurrentDate().getTime()) ? overexposureTime : null;
	}

	get finishInDuration() {
		const finishTime = this.programState == Program_State.STARTED ? this.programFinishTime : null
		if (finishTime == null) return null;
		return msToDuration(finishTime - getCurrentDate().getTime());
	}

	get estimateDuration(): string {
		const durationMs = this.supplier.data.programEstimate
		if (!durationMs) return ''

		const duration = msToDuration(durationMs)
		duration.seconds = 0
		return `~` + durationTextAround(messages, duration)
	}

	get duration(): string {
		let finish = this.supplier.update?.finishProgramTime || this.supplier.data.programStart
		let start = this.supplier.update?.startProgramTime || this.supplier.data.programFinish
		if (finish == null || start == null) return ''

		const duration = msToDuration(finish - start)
		duration.seconds = 0
		return durationTextAround(messages, duration)
	}

	get positionNowString(): string {
		const position = this.position
		if (position == null) return ''

		let name = this.supplier.getPositionShortTitle(position)
		if (name) {
			name = ` (${name})`
		}
		return `${position}${name}`
	}

	get stepEstTimeString(): string {
		const step = this.stepDonePair
		if (step == null || step.current == null) return ''

		const processTime = coalesce(this.supplier.update?.paProcessTime, null)
		if (processTime == null) return '';

		return `${Math.round(processTime / 1000)}`;
	}

	get stepDurTimeString(): string {
		const step = this.stepDonePair
		if (step == null || step.current == null) return ''

		const processTime = coalesce(this.supplier.update?.paProcessTime, null)
		if (processTime == null) return '';

		const timeMin = (this.programActionMinTs || [])[step.current]
		const timeMax = (this.programActionMaxTs || [])[step.current]
		if (timeMin == null && timeMax == null) {
			return '';
		}
		if (timeMin == null) return `<${timeMax}`;
		if (timeMax == null) return `${timeMin}+`;
		return `${timeMin}-${timeMax}`;
	}

	get stepRunoffTime(): number {
		return zeroWhenNaN(this.supplier.update?.paRunoffTime);
	}

	get stepNextPosition(): number | null {
		return nullWhenNaN(coalesce(this.positionNextPath, [])[0])
	}

	/**
	 * Возвращает текущие точки (позиции) пути до следующей точки назначения (до ванны или накопителя, например).
	 * Текущая позиция - не добавляется
	 */
	get positionNextPath(): number[] | null {
		const path = this.supplier.update?.pendingPath;
		if (!path || path.length == 0) {
			return null;
		}

		const currentPosition = this.position;
		// убираем из пути текущую позицию
		const pathWithoutCurrent = path.filter(p => currentPosition == null || p != currentPosition);
		return pathWithoutCurrent.length ? pathWithoutCurrent : null;
	}

	get positionNextString(): string {
		const path = this.supplier.update?.pendingPath;
		if (path == null || path.length == 0) return '';

		const next = path[path.length - 1];

		let name = this.supplier.getPositionShortTitle(next)
		if (name) {
			name = ` (${name})`
		}
		return `${next}${name}`
	}

	get positionEndString(): string {
		const finish = this.supplier.update?.finishProgramPosition
		if (!finish) return ''

		let name = this.supplier.getPositionShortTitle(finish)
		if (name) {
			name = ` (${name})`
		}
		return `${finish}${name}`
	}

	get enteredTarget(): number | null {
		return this.supplier.update?.enteredTargetPosition || null;
	}

	get enteredTargetString(): string {
		if (!this.programByProcess || this.programState != Program_State.STARTED) return '';

		const entered = this.supplier.update?.enteredTargetPosition || null
		if (!entered) {
			return messages.get('UpdateContainerMessage.enteredTargetPosition.any')
		}
		let name = this.supplier.getPositionShortTitle(entered)
		if (name) {
			name = ` (${name})`
		}
		return `${entered}${name}`
	}

	get stepDonePair(): { current: number | null, total: number } | null {
		if (this.programState != Program_State.STARTED && this.programState != Program_State.TERMINATED || this.supplier.data.program != this.supplier.update?.lastProgramId) return null;

		const processActions = this.supplier.update?.processActions
		if (processActions == null) return null

		return {current: coalesce(this.supplier.update?.paIndex, null), total: processActions};
	}

	get stepDoneString(): string {
		const donePair = this.stepDonePair;
		if (donePair == null) return '';

		const currentIndex = donePair.current == null ? '-' : donePair.current;
		return `${currentIndex} / ${donePair.total}`;
	}

	/**
	 * @returns `[0..1] `: если шаг в процессе. `1`: если шаг был начат и закончен. `null`: если статус неизвестен
	 */
	stepDonePercent(currentTime: number | null): number | null {
		const update = this.supplier.update;
		if (!update || update.currentTime == null || update.paIndex == null)
			return null;

		// paIndex выставлен, если начался, проходит, или уже закончен какой-то шаг обработки.
		// Если psStartTime + paProcessTime - дают время окончания шага, даже есть paProcessTime == 0
		// Если psStartTime == null - значит обработка закончилась, можно помечать этот шаг, как пройденный

		if (update.paStartTime == null || update.paProcessTime == null) return 1;

		const endOfPositionProcess = update.paStartTime + update.paProcessTime;

		if (currentTime == null)
			currentTime = getCurrentDate().getTime()

		if (endOfPositionProcess <= currentTime || update.paProcessTime <= 0) {
			return 1;
		}

		// console.info(`currentTime=${currentTime}, paStartTime=${update.paStartTime}, paProcessTime=${update.paProcessTime}, diff=${currentTime - update.paStartTime}`)
		return Math.min(1, (currentTime - update.paStartTime) / (update.paProcessTime))
	}

	get cancelStartingAvailable(): boolean {
		const update = this.supplier.update
		if (update == null || update.lastCancelTime == null) return false;

		const lastCancelTime = coalesce(update.lastCancelTime, null);
		return lastCancelTime != null && getCurrentDate().getTime() < lastCancelTime;
	}
}
